import React, { useState, useEffect, useId } from 'react';
import axios from 'axios';
import './RouteForm.css';

const LocationIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" />
    </svg>
);

function RouteForm({ setResults, setLoading, setError }) {
    const [formData, setFormData] = useState(() => {
        const saved = sessionStorage.getItem('tankly_form_data');
        if (saved) return JSON.parse(saved);
        return {
            fromLocation: '', toLocation: '', fuelType: 'DIESEL', tankSize: '',
            currentFuelLevel: 50, averageConsumption: '', maxDetour: 2000,
            isTruck: false, euroClass: 'EURO_6', co2Class: 'CLASS_1',
        };
    });

    useEffect(() => {
        sessionStorage.setItem('tankly_form_data', JSON.stringify(formData));
    }, [formData]);
    const [suggestions, setSuggestions] = useState({ from: [], to: [] });
    const [isSelected, setIsSelected] = useState(() => {
        const saved = sessionStorage.getItem('tankly_form_data');
        if (saved) {
            const parsed = JSON.parse(saved);
            return {
                from: !!parsed.fromLocation,
                to: !!parsed.toLocation
            };
        }
        return { from: false, to: false };
    });
    const [showSuggestions, setShowSuggestions] = useState({ from: false, to: false });
    const fromListId = useId();
    const toListId = useId();

    useEffect(() => {
        const t = setTimeout(() => {
            if (formData.fromLocation.length >= 3 && !isSelected.from) fetchSuggestions(formData.fromLocation, 'from');
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.fromLocation]);

    useEffect(() => {
        const t = setTimeout(() => {
            if (formData.toLocation.length >= 3 && !isSelected.to) fetchSuggestions(formData.toLocation, 'to');
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.toLocation]);

    const fetchSuggestions = async (query, field) => {
        if (query.length < 3) return;
        const API_KEY = process.env.REACT_APP_IQ_KEY;
        try {
            const response = await fetch(
                `https://api.locationiq.com/v1/autocomplete?key=${API_KEY}&q=${encodeURIComponent(query)}&limit=5&countrycodes=de,at,ch,rs,hr&accept-language=en`
            );
            const data = await response.json();
            setSuggestions(prev => ({ ...prev, [field]: data }));
            setShowSuggestions(prev => ({ ...prev, [field]: true }));
        } catch (err) {
            console.error('Cannot reach LocationIQ.', err);
        }
    };

    const handleSelectSuggestion = (suggestion, field) => {
        setIsSelected(prev => ({ ...prev, [field]: true }));
        setFormData(prev => ({ ...prev, [field === 'from' ? 'fromLocation' : 'toLocation']: suggestion.display_name }));
        setSuggestions(prev => ({ ...prev, [field]: [] }));
        setShowSuggestions(prev => ({ ...prev, [field]: false }));
    };

    const getCurrentLocation = () => {
        if (!navigator.geolocation) {
            setError('Geolocation is not supported by your browser.');
            return;
        }
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                const API_KEY = process.env.REACT_APP_IQ_KEY;
                try {
                    const response = await fetch(
                        `https://api.locationiq.com/v1/reverse?key=${API_KEY}&lat=${latitude}&lon=${longitude}&format=json&accept-language=en`
                    );

                    if (response.ok) {
                        const data = await response.json();
                        setIsSelected(prev => ({ ...prev, from: true }));
                    setFormData(prev => ({ ...prev, fromLocation: data.display_name || `${latitude},${longitude}` }));
                } else {
                    setIsSelected(prev => ({ ...prev, from: true }));
                    setFormData(prev => ({ ...prev, fromLocation: `${latitude},${longitude}` }));
                }
            } catch (error) {
                console.error('Error fetching reverse geocoding:', error);
                setIsSelected(prev => ({ ...prev, from: true }));
                setFormData(prev => ({ ...prev, fromLocation: `${latitude},${longitude}` }));
            }
        },
            (err) => {
                const messages = {
                    1: 'Location access was denied. You can type an address instead.',
                    2: 'Your location is not available right now.',
                    3: 'Finding your location timed out. Try again.',
                };
                setError(messages[err.code] || 'We could not get your location.');
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setResults(null);
        try {
            const rawUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            let baseUrl = rawUrl.trim();
            if (!baseUrl.startsWith('http')) baseUrl = `https://${baseUrl}`;
            const finalUrl = `${baseUrl.replace(/\/$/, '')}/api/routes`;
            const token = localStorage.getItem('tankly_token');
            const response = await axios.post(finalUrl, formData, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.data.recommendedStations || response.data.recommendedStations.length === 0) {
                setError('No stations found along your route. Try adjusting your fuel level or route.');
            } else {
                setResults(response.data);
            }
        } catch (err) {
            if (err.response?.status === 429) setError('Too many requests. Please wait a minute and try again.');
            else if (err.response?.status === 500) setError('We could not find that location. Check your from/to fields.');
            else setError('Something went wrong. Check your connection and try again.');
        }
        setLoading(false);
    };

    const fuelPct = Number(formData.currentFuelLevel);
    const gaugeColor = fuelPct <= 15 ? 'var(--signal-red)' : fuelPct <= 40 ? 'var(--amber)' : 'var(--diesel-green)';

    return (
        <form className="route-form" onSubmit={handleSubmit} aria-label="Find the best fuel station along your route">
            <fieldset className="form-section">
                <legend>Route</legend>

                <div className="field">
                    <label htmlFor="from-location">From</label>
                    <div className="autocomplete-wrapper">
                        <input
                            id="from-location"
                            role="combobox"
                            aria-expanded={showSuggestions.from && suggestions.from.length > 0}
                            aria-controls={fromListId}
                            aria-autocomplete="list"
                            type="text"
                            placeholder="Start address or city"
                            value={formData.fromLocation}
                            onChange={(e) => {
                                setIsSelected(prev => ({ ...prev, from: false }));
                                setFormData({ ...formData, fromLocation: e.target.value });
                            }}
                            onFocus={() => setShowSuggestions(prev => ({ ...prev, from: true }))}
                            onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, from: false })), 150)}
                        />
                        {showSuggestions.from && suggestions.from.length > 0 && (
                            <ul className="suggestions-list" id={fromListId} role="listbox">
                                {suggestions.from.map((s, i) => (
                                    <li key={i} role="option" aria-selected="false" onMouseDown={() => handleSelectSuggestion(s, 'from')}>
                                        {s.display_name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <button type="button" className="button-location" onClick={getCurrentLocation}>
                        <LocationIcon /> Use my current location
                    </button>
                </div>

                <div className="field">
                    <label htmlFor="to-location">To</label>
                    <div className="autocomplete-wrapper">
                        <input
                            id="to-location"
                            role="combobox"
                            aria-expanded={showSuggestions.to && suggestions.to.length > 0}
                            aria-controls={toListId}
                            aria-autocomplete="list"
                            type="text"
                            placeholder="Destination address or city"
                            value={formData.toLocation}
                            onChange={(e) => {
                                setIsSelected(prev => ({ ...prev, to: false }));
                                setFormData({ ...formData, toLocation: e.target.value });
                            }}
                            onFocus={() => setShowSuggestions(prev => ({ ...prev, to: true }))}
                            onBlur={() => setTimeout(() => setShowSuggestions(prev => ({ ...prev, to: false })), 150)}
                        />
                        {showSuggestions.to && suggestions.to.length > 0 && (
                            <ul className="suggestions-list" id={toListId} role="listbox">
                                {suggestions.to.map((s, i) => (
                                    <li key={i} role="option" aria-selected="false" onMouseDown={() => handleSelectSuggestion(s, 'to')}>
                                        {s.display_name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </fieldset>

            <fieldset className="form-section">
                <legend>Vehicle</legend>

                <div className="field">
                    <label htmlFor="fuel-type">Fuel type</label>
                    <select id="fuel-type" value={formData.fuelType} onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}>
                        <option value="DIESEL">Diesel</option>
                        <option value="PETROL">Petrol</option>
                    </select>
                </div>

                <div className="field">
                    <label htmlFor="tank-size">Tank size (liters)</label>
                    <input
                        id="tank-size" type="text" inputMode="decimal" placeholder="e.g. 70"
                        value={formData.tankSize}
                        onChange={(e) => {
                            const val = e.target.value.replace(/,/g, '.');
                            if (/^\d*\.?\d*$/.test(val)) {
                                setFormData({ ...formData, tankSize: val });
                            }
                        }}
                    />
                </div>

                <div className="gauge-field">
                    <div className="gauge-label-row">
                        <label htmlFor="fuel-level">Current fuel level</label>
                        <span className="gauge-readout" style={{ color: gaugeColor }}>{fuelPct}%</span>
                    </div>
                    <div className="gauge-track-wrap">
                        <input
                            id="fuel-level" type="range" min="0" max="100"
                            value={formData.currentFuelLevel}
                            onChange={(e) => setFormData({ ...formData, currentFuelLevel: e.target.value })}
                            className="fuel-slider"
                            aria-describedby="fuel-level-ticks"
                        />
                    </div>
                    <div className="gauge-ticks" id="fuel-level-ticks" aria-hidden="true">
                        <span>E</span><span>¼</span><span>½</span><span>¾</span><span>F</span>
                    </div>
                </div>

                <div className="field">
                    <label htmlFor="avg-consumption">Average consumption (L/100km)</label>
                    <input
                        id="avg-consumption" type="text" inputMode="decimal" placeholder="e.g. 7.5"
                        value={formData.averageConsumption}
                        onChange={(e) => {
                            const val = e.target.value.replace(/,/g, '.');
                            if (/^\d*\.?\d*$/.test(val)) {
                                setFormData({ ...formData, averageConsumption: val });
                            }
                        }}
                    />
                </div>

                <label className="toggle-container" htmlFor="is-truck">
                    <input
                        id="is-truck" type="checkbox"
                        checked={formData.isTruck}
                        onChange={(e) => setFormData({ ...formData, isTruck: e.target.checked })}
                    />
                    <span className="toggle-slider" aria-hidden="true"></span>
                    <span className="toggle-text">This is a truck</span>
                </label>

                {formData.isTruck && (
                    <div className="truck-options">
                        <div className="field">
                            <label htmlFor="euro-class">Emission class (Euro)</label>
                            <select id="euro-class" value={formData.euroClass} onChange={(e) => setFormData({ ...formData, euroClass: e.target.value })}>
                                <option value="EURO_6">Euro VI</option>
                                <option value="EURO_5">Euro V</option>
                                <option value="EURO_4">Euro IV</option>
                            </select>
                        </div>
                        <div className="field">
                            <label htmlFor="co2-class">CO2 class</label>
                            <select id="co2-class" value={formData.co2Class} onChange={(e) => setFormData({ ...formData, co2Class: e.target.value })}>
                                <option value="CLASS_1">Class 1 (Diesel standard)</option>
                                <option value="CLASS_2">Class 2 (Optimized)</option>
                                <option value="CLASS_5">Class 5 (Zero emission / electric)</option>
                            </select>
                        </div>
                    </div>
                )}
            </fieldset>

            <button type="submit" className="btn-primary route-submit">Find best station</button>
        </form>
    );
}

export default RouteForm;